import { useQuery } from '@apollo/client';
import { Dropdown, TextArea, useDialog } from '@ics-portal/guardian';
import { type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { GET_COMPONENTS_SOURCES } from '@api/queries/components';
import { SearchComponentsTable } from '@components/NewComponentCreateRequestTrigger/components/SearchComponentsDialog/components/SearchComponentsTable';
import { ComponentCreateRequestStatusCode } from '@gql/graphql';
import { useNewComponentRequestActions } from '@hooks/NewComponentRequests/useNewComponentRequestActions';
import { type StringOption } from '@models/Option';
import { getActionDialogName } from '@pages/notification-components/components/RequestsCard/RequestsTable/utils/getActionDialogName';
import { type RequestRow } from '@pages/notification-components/components/RequestsCard/RequestsTable/columns';
import { simplePluralize } from '@utils/stringFormatUtils';

export interface UpdateComponentRequestStatusForm {
    action: ComponentCreateRequestStatusCode;
    componentCreateRequest: RequestRow;
    comment: string;
    source?: string;
    componentID?: string;
}

interface ActionDialogProps {
    action: ComponentCreateRequestStatusCode;
    selectedRows: RequestRow[];
}

export async function submitRequestStatusUpdates(
    updateStatus: (
        formValue: Partial<UpdateComponentRequestStatusForm>,
    ) => Promise<void>,
    action: ComponentCreateRequestStatusCode,
    formValue: Partial<UpdateComponentRequestStatusForm>,
    selectedRows: RequestRow[],
): Promise<void> {
    await Promise.all(
        selectedRows.map((componentCreateRequest) =>
            updateStatus({
                ...formValue,
                action,
                componentCreateRequest,
            }),
        ),
    );
}

export function ActionDialog(props: ActionDialogProps): ReactNode {
    const { action, selectedRows } = props;

    const { close, handleSubmit: bindDialogSubmit, setDisabled } = useDialog();
    const { updateStatus } = useNewComponentRequestActions();
    const { data: componentsSourceData, loading: sourcesLoading } = useQuery(
        GET_COMPONENTS_SOURCES,
        {
            skip: action !== ComponentCreateRequestStatusCode.Forwarded,
        },
    );

    const componentsSourcesOptions: StringOption[] = useMemo(
        () =>
            componentsSourceData?.componentSources.edges
                .map((edge) => edge.node)
                .map((node) => ({
                    value: node.id,
                    label: node.name,
                })) ?? [],
        [componentsSourceData],
    );

    const form = useForm<UpdateComponentRequestStatusForm>({
        mode: 'onChange',
        defaultValues: {
            comment: '',
            source: '',
            componentID: undefined,
        },
    });
    const { handleSubmit, register, setValue, formState, watch } = form;

    const submitHandler = useCallback(
        async (formValue: UpdateComponentRequestStatusForm): Promise<void> => {
            await submitRequestStatusUpdates(
                updateStatus,
                action,
                formValue,
                selectedRows,
            );
            close();
        },
        [action, close, selectedRows, updateStatus],
    );

    useEffect(() => {
        // Dialog provider exposes non-memoized handlers; register once like
        // the working dialogs in the app to avoid provider rebind loops.
        bindDialogSubmit(() => handleSubmit(submitHandler)());
        // eslint-disable-next-line react/exhaustive-deps
    }, []);

    useEffect(() => {
        const hasDirtyFields = Object.keys(formState.dirtyFields).length > 0;
        setDisabled(!formState.isValid || !hasDirtyFields);
    }, [formState.dirtyFields, formState.isValid, setDisabled]);

    useEffect(() => {
        register('componentID', {
            validate: (v) =>
                action !== ComponentCreateRequestStatusCode.Replaced ||
                Boolean(v) ||
                'Component is required',
        });
    }, [register, action]);

    useEffect(() => {
        if (action !== ComponentCreateRequestStatusCode.Replaced) {
            setValue('componentID', undefined);
        }

        if (action !== ComponentCreateRequestStatusCode.Forwarded) {
            setValue('source', '');
        }
    }, [action, setValue]);

    const selectedComponentId = watch('componentID');

    return (
        <div className="flex flex-column gap-4 h-full">
            <div className="flex flex-column gap-2">
                <p className="pb-0">
                    Are you sure you want to{' '}
                    {getActionDialogName(action).toLowerCase()} selected
                    component {simplePluralize('request', selectedRows.length)}?
                </p>
                <p className="pb-0">Provide a comment with justification.</p>
            </div>
            <div className="flex flex-column h-full gap-2">
                <FormProvider {...form}>
                    {action === ComponentCreateRequestStatusCode.Forwarded && (
                        <Dropdown
                            label="Source"
                            placeholder="Select source"
                            options={componentsSourcesOptions}
                            disabled={sourcesLoading}
                            name="source"
                            validate={(value) =>
                                action !==
                                    ComponentCreateRequestStatusCode.Forwarded ||
                                Boolean(value) ||
                                'Source is required'
                            }
                        />
                    )}

                    <TextArea
                        label="Comment"
                        placeholder="Enter comment"
                        required
                        name="comment"
                    />

                    {action === ComponentCreateRequestStatusCode.Replaced && (
                        <SearchComponentsTable
                            tableId="search-components-replace-request"
                            onSelectionChanged={(rows) => {
                                setValue('componentID', rows[0]?.id, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                });
                            }}
                        />
                    )}
                </FormProvider>
                {action === ComponentCreateRequestStatusCode.Replaced &&
                !selectedComponentId ? (
                    <small className="p-error">Component is required</small>
                ) : null}
            </div>
        </div>
    );
}
